import { useRef } from 'react';

import { SourceStore, useSourceStore } from '@stores/useSourceStore';
import snakeize from 'snakeize';

import { DOMAIN, PurchaseType, StoryWaitfreeStatus } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { checkRequiredAppVersion, isEnv, jsonStringify } from '@utils/misc';

import { BundlePassType, CurrencyType, VendorType } from './../model.d';

export type ChatieIOSInterface = {
  postMessage: (message: object) => void;
};

export type AnalyticsWebInterface = {
  logEvent: (name: string, data: string) => void;
  setUserProperty: (name: string, data: string | number) => void;
};

export type OneSignalWebInterface = {
  addTag: (name: string, value: string) => void;
  addTrigger: (name: string, value: string) => void;
};

type Value = string | number | boolean;

type Params = { [key: string]: Value | null };

type DataManagementTypes = 'firebase' | 'oneSignal';

type AndroidInterfaces = AnalyticsWebInterface | OneSignalWebInterface;

type PendingEvent = {
  name: keyof FirebaseEvents | keyof OneSignalEvents;
  data: string;
};

const GA_KEYS: Record<string, string> = {
  [DOMAIN.chatie]: '',
  [DOMAIN.cheming]: process.env.NEXT_PUBLIC_FIREBASE_CHEMING_GTAG || '',
};

export class IOSInterface {
  private interface?: ChatieIOSInterface;

  constructor(public key: DataManagementTypes) {
    if (typeof window !== 'undefined') {
      this.interface = window.webkit?.messageHandlers?.chatie;
    }
  }

  log = (handlerName: string, name: string | undefined, data: object) => {
    if (this.interface) {
      const command = `${this.key}:${handlerName}`;
      const message = {
        command,
        name,
        ...data,
      };
      this.interface.postMessage(message);
    }
  };
}

function makeData(params: Params) {
  return {
    android: jsonStringify(params),
    ios: snakeize(params) as Params,
  };
}

function log(key: string, name: string, data: object) {
  if (!isEnv('production')) {
    console.log(`%c${key}`, 'color: #20a2ae;', name, data);
  }
}

interface DataManagementInterface {
  androidInterface?: AndroidInterfaces;
  iosInterface: IOSInterface;
  store: SourceStore;
}

/** Firebase */
export type FirebaseEvents = {
  home_tab_click: {
    tab: string;
  };
  home_curation_click: {
    title: string;
  };
  story_inflow: {
    storyId: number;
    userId: number;
    userCreated: string;
    path: string;
  };
  story_ad_impression: {
    storyId: number;
    storyAdId: number;
    where: 'interstitial';
  };
  story_ad_click: {
    storyId: number;
    storyAdId: number;
    where: 'curation' | 'episode' | 'interstitial' | 'search';
  };
  create_content: {
    contentType: 'story' | 'episode' | 'community' | 'comment';
    itemId: number;
    episodeId?: number;
    platform?: string;
    appVersion?: string;
    from?:
      | 'feed'
      | 'community'
      | 'play'
      | 'chat'
      | 'comments'
      | 'episode'
      | 'event';
  };
  rewrite_content: {
    contentType: 'episode';
    itemId: number;
  };
  edit_content: {
    contentType: 'story' | 'episode' | 'community';
    itemId: number;
  };
  delete_content: {
    contentType: 'story' | 'episode' | 'community' | 'comment';
    itemId: number;
  };
  search: {
    userId: number;
    searchTerm: string;
  };
  booking_content: {
    contentType: 'episode';
    storyId: number;
    itemId: number;
    booked: string;
    pushType: 'DF' | 'CUS';
    pushBody?: string;
  };
  booking_cancel_content: {
    contentType: 'episode';
    storyId: number;
    itemId: number;
  };
  publish_content: {
    contentType: 'episode';
    itemId: number;
  };
  episode_end_view_mode: {
    view_mode: 'scroll' | 'tap';
  };
  editor_usage: {
    storyId: number;
    episodeId: number;
    addCount: number;
    editCount: number;
    deleteCount: number;
  };
  continue_read: {
    storyId: number;
  };
  select_tag: {
    tagId: number;
    tagName: string;
    from: string;
  };

  // instant
  estimated_revenue_open: {
    storyId: number;
    openDate: number;
    bookingEpisodeCount: number;
  };
  estimated_revenue_click: {
    storyId: number;
  };
  random_item_purchase: {
    itemId: number;
    itemOptionId: number;
  };
  one_time_skip_ad: {
    storyId: number;
    episodeId: number;
    from: string;
  };

  // waitfree
  toggle_waitfree_story: {
    status: StoryWaitfreeStatus;
    storyId: number;
    blockEpisodeCount: number;
    freeEpisodeCount: number;
  };
  update_waitfree_story: {
    storyId: number;
    blockEpisodeCount: number;
    freeEpisodeCount: number;
  };
  waitfree_inactive_open: {
    storyId: number;
  };
  waitfree_inactive: {
    storyId: number;
    reason: string;
  };
  waitfree_breakaway: {
    storyId: number;
    reason: string;
  };
  waitfree_breakaway_open: {
    storyId: number;
  };

  // episode
  episode_end_popup: {
    type: 'last' | 'default';
    storyId: number;
    episodeId: number;
  };
  unfollow_content: {
    contentType: 'user';
    itemId: number;
    where: 'episode' | 'story';
  };
  follow_content: {
    contentType: 'user';
    itemId: number;
    where: 'episode' | 'story';
  };
  select_content: {
    contentType: 'episode';
    itemId: number;
  };
  read_content: {
    userId: number;
    contentType: 'episode';
    storyId: number;
    itemId: number;
    tapCount: number;
    started: string;
    ended: string;
    source?: string;
  };

  // offerwall
  induce_offerwall_click: {
    type: string;
  };

  // donation
  sponsor: {
    price: number;
    storyId: number;
    episodeId?: number;
  };
  preview_content: {
    contentType: 'episode';
    storyId: number;
    episodeId: number;
    balloon: number;
  };

  // app review
  app_review: {
    answer: 'yes' | 'no' | 'later' | 'far_later';
  };

  //challenge
  publish_challenge: {
    userId: number;
    storyId: number;
    episodeId: number;
    date: string;
    beforeStatus: 'nothing' | 'prepare' | 'in_progress';
    afterStatus: 'nothing' | 'prepare' | 'in_progress';
  };
  leave: {
    reasons: string | null;
  };

  // chatbot
  action_bt: {
    time: string;
    user_id: number;
    chat_room_id: number;
    is_active: boolean;
  };
  refresh_bt: {
    time: string;
    user_id: number;
    chat_room_id: number;
    chat_id: string;
  };
  chat_room_inactive: {
    inactivation_count: number;
    total_count: number;
    user_id: number;
  };
  chat_send: {
    user_id: number;
    mode: 'continue' | 'normal';
    chat_room_id: number;
    is_first: boolean;
  };
  chatbot_enter_tutorial: {
    is_first: boolean;
  };
  chatbot_click_tutorial_button: {
    button_type: 'start' | 'skip';
    is_first: boolean;
  };

  //챗봇 베타
  chatbot_click_play_curation: {
    curation_view_type: CurationViewType;
  };
  chatbot_purchase_play_curation_situation: {
    curation_view_type: CurationViewType;
    character_id: number;
    situation_id: number;
    story_id: number;
  };
  chatbot_purchase_character_situation: {
    situation_id: number;
    character_id: number;
    story_id: number;
  };
  chatbot_click_character_btn: {
    btn_type: 'like' | 'chat' | 'story' | 'situation';
    has_chat: boolean;
    has_chat_with_character: boolean;
  };
  chatbot_entrance_character_library: {
    character_id: number;
  };
  chatbot_click_character_library_chat_btn: {
    chat_room_id: number;
    character_id: number;
  };
  chatbot_click_library_like_chat: {
    btn_type: 'profile' | 'chat';
    character_id: number;
  };
  challenge_waitfree_publish: {
    story_id: number;
    user_id: number;
    episode_id: number;
    date: string;
    reward_ratio: number;
  };
  challenge_prieview_pusblish: {
    story_id: number;
    user_id: number;
    episode_id: number;
    date: string;
    reward_ratio: number;
  };

  select_play: Record<string, never>;

  chatbot_character_inflow: {
    character_id: number;
    source: string;
  };
  chatbot_room_inflow: {
    object_type: 'chat_room' | 'situation';
    object_id: number;
    source: string;
  };
  chatbot_situation_list_inflow: {
    character_id: number;
    source: string;
  };
  chatbot_open_modal_buy_situation: {
    situation_id: number;
    character_id: number;
    source: string;
  };
  select_payment: {
    inflow: 'modal_buy_situation';
  };
  test_chatbot_character_detail_action: {
    target: 'like_btn' | 'chat' | 'story' | 'situation';
    action_type: 'click';
  };
  test_chatbot_chat_delete: {
    chat_room_id: number;
    character_id: number;
  };
  test_chatbot_chat_modify: {
    chat_room_id: number;
    character_id: number;
    edited_content: string;
    origin_content: string;
  };
  test_chatbot_chat_swipe: {
    situation_id: number;
    swipe_direction: 'left' | 'right';
  };
  chatbot_playground_inflow: {
    character_id: number;
    source: string;
  };
  chatbot_chat_room_create: {
    chat_room_id: number;
    is_sexual: boolean;
    character_id: number;
    source?: string;
  };
  test_chatbot_moderation_error: {
    content: string;
  };
  test_chatbot_message_error: {
    result_code?: string;
    result_message?: string;
    result_errors?: string;
    status_code?: number;
  };
  test_chatbot_first_message: {
    chat_room_id: number;
    select: boolean;
  };
  chatbot_chat_room_capture: {
    chat_room_id: number;
    character_id: number;
  };
  open_payment_modal: {
    group: 'A' | 'B';
  };
  payment_modal_btn_click: {
    group: 'A' | 'B';
    btn_type: '충전소' | '바로충전';
    purchase_type: keyof typeof PurchaseType | null;
    is_default_item: boolean | null;
    price?: number;
  };
  peek_first_activate: {
    date: string;
    chat_room_id: number;
  };
  peek_unactivate: {
    date: string;
    chat_room_id: number;
  };
  peek_tap: {
    tap_count: number;
    chat_room_id: string;
  };
  peek_entrance: {
    character_id: number;
    chat_room_id: string;
    source: string;
  };
  purchase: {
    id: number;
    description: string;
    bundleId: string;
    type: 'PT' | 'SB';
    purchaseType: keyof typeof PurchaseType;
    quantity: number;
    bonusType: 'PT';
    bonusQuantity: number;
    price: number;
    discountRatio: number;
    currency: CurrencyType;
    uuid: string | null;
    method: string;
    prev_path: string | null;
  };
  purchase_bundle_pass: {
    id: number;
    vendor: VendorType;
    bundlePassType: BundlePassType;
    description: string;
    bundleId: string;
    price: number;
    discountRatio: number;
    currency: CurrencyType;
    uuid: string | null;
    prev_path: string | null;
  };
  free_chat_modal_entrance: {
    character_id: number | string;
  };
  free_chat_modal_click: {
    character_id: number | string;
  };
  episode_chat_send_modal_entrance: {
    story_id: number | string;
    episode_id: number | string;
  };
  click_recommend_chat: {
    content: string;
    chat_room_id?: number;
  };
  send_recommend_chat: {
    content: string;
    chat_room_id?: number;
  };
  show_recommend_chat: {
    message_count: number;
  };
  help_plot_developments: {
    story_id: number;
    episode_id: number;
    is_promotion_request: boolean;
    direction: string;
    development_type: 'prev_episode' | 'curr_episode';
  };
  help_plot_recommend: {
    type: 'refresh' | 'request';
    story_id: number;
    episode_id: number;
    is_promotion_request: boolean;
    recommend_type:
      | 'prev_episode'
      | 'curr_episode'
      | 'story_summary'
      | 'base_plot';
  };
  chapter_edit_character: Record<string, never>;
  chapter_edit_character_confirm: Record<string, never>;
  shit_icon_click: Record<string, never>;
  shit_certification_click: {
    action: 'update' | 'certification' | 'move';
  };
  shit_certification: {
    type: 'AC' | 'IAC' | 'FL';
  };
  ad_block: Record<string, never>;
  share_click: {
    type: 'story' | 'episode' | 'honor';
    storyId?: number;
    episodeId?: number;
    url: string;
    source: string;
  };
  help_plot_developments_modal_open: Record<string, never>;
  help_plot_developments_click: {
    letter_count: number;
    can_develop: boolean;
  };
  playbox_pass_purchase: object;
  sns_share: {
    room_type: 'normal' | 'private';
  };
  event_page_click: object;
  attendance_click: object;
  character_library_click: object;
  invitation_click: object;
  story_character_click: { story_id: number; character_id: number | string };
  click_character_detail_tab: { tap_type: string; character_id: number };
  click_episode_chat_enter: {
    episode_id: number;
    inflow: string; //'episode_detail' | 'episode_end' | 'episode_exit';
    is_last_episode: boolean;
  };
  click_episode_chat_btn: {
    episode_id: number;
    btn_type: string; //'episode_detail' | 'episode_end' | 'episode_exit';
    is_last_episode: boolean;
  };
  show_episode_chat_cta: {
    episode_id: number;
    type: string; //'episode_detail' | 'episode_end' | 'episode_exit';
    is_last_episode: boolean;
  };
  free_episode_chat_enter: {
    episode_id: number;
    story_id: number;
    free_chat_count: number;
    preset_id: number;
  };
  free_episode_chat_ad_trigger: {
    episode_id: number;
    story_id: number;
    free_chat_count: number;
  };
  free_episode_chat_ad_interstitial_shown: {
    episode_id: number;
    story_id: number;
    free_chat_count: number;
  };
  free_episode_chat_ad_interstitial_failed: {
    episode_id: number;
    story_id: number;
    free_chat_count: number;
  };
  free_episode_chat_ad_story_ad_shown: {
    episode_id: number;
    story_id: number;
    story_ad_id: number;
    free_chat_count: number;
  };
  free_episode_chat_ad_all_failed: {
    episode_id: number;
    story_id: number;
    free_chat_count: number;
  };
  free_episode_chat_message_sent: {
    episode_id: number;
    story_id: number;
    free_chat_count: number;
    chat_room_id: number;
  };
  free_episode_chat_room_exit: {
    episode_id: number;
    story_id: number;
    chat_room_id: number;
    message_count: number;
  };
  free_episode_chat_modal_close: {
    episode_id: number;
    story_id: number;
    free_chat_count: number;
  };
  show_daily_pass_banner: {
    type: string; // 'home'| 'preview_banner'| 'waitfree_banner'| 'payment'
  };
  click_daily_pass_banner: {
    type: string; //'home'| 'preview_banner'| 'waitfree_banner'| 'payment'
    episode_id?: number;
  };
  click_purchase_daily_pass: {
    inflow: string; //'home'| 'preview_banner'| 'waitfree_banner'| 'payment'
    episode_id?: number;
  };
  roulette_ad_init: {
    ad_type: string;
    provider: string;
    supports_reward: boolean;
    platform: string;
    app_version?: string;
  };
  roulette_ad_loaded: {
    ad_type: string;
    platform: string;
    app_version?: string;
  };
  roulette_ad_load_failed: {
    ad_type: string;
    platform: string;
    app_version?: string;
  };
  roulette_ad_spin_attempt: {
    ad_type: string;
    ad_ready: boolean;
    ad_status: number;
    platform: string;
    app_version?: string;
  };
  roulette_ad_timeout: {
    ad_type: string;
    platform: string;
    app_version?: string;
  };
  roulette_ad_rewarded: {
    platform: string;
    app_version?: string;
  };
  roulette_ad_closed: {
    reward_earned: boolean;
    supports_reward: boolean;
    platform: string;
    app_version?: string;
  };
  // 에피소드 광고 이벤트
  episode_ad_trigger: {
    story_id: number;
    episode_id: number;
    tap_count: number;
    platform: string;
    app_version?: string;
  };
  episode_ad_interstitial_shown: {
    story_id?: number;
    episode_id?: number;
    platform: string;
    app_version?: string;
  };
  episode_ad_story_ad_shown: {
    story_id?: number;
    episode_id?: number;
    story_ad_id: number;
    platform: string;
    app_version?: string;
  };
  episode_ad_all_failed: {
    story_id?: number;
    episode_id?: number;
    ad_ready: boolean;
    platform: string;
    app_version?: string;
  };
  episode_ad_skip_modal_watch: {
    story_id?: number;
    episode_id?: number;
    ad_ready: boolean;
    platform: string;
    app_version?: string;
  };
  episode_ad_skip_modal_dismissed: {
    story_id?: number;
    episode_id?: number;
    reason: string;
    platform: string;
    app_version?: string;
  };
  episode_ad_load_timeout: {
    story_id?: number;
    episode_id?: number;
    platform: string;
    app_version?: string;
  };
  episode_ad_skip_failed: {
    story_id: number;
    episode_id: number;
    balloon: number;
    platform: string;
    app_version?: string;
  };
  episode_ad_remove_item_used: {
    story_id: number;
    episode_id: number;
    inventory_id: number;
  };
};

export type CurationViewType =
  | 'full_screen_banner’ | ‘profile_gallery’ |‘content_gallery_with_header'
  | 'divider_line';

class FirebaseInterface implements DataManagementInterface {
  androidInterface?: AnalyticsWebInterface;
  iosInterface: IOSInterface;
  store: SourceStore;

  private static instance: FirebaseInterface;

  public static getInstance(sourceStore: SourceStore) {
    return this.instance || (this.instance = new this(sourceStore));
  }

  pendingEvents: PendingEvent[] = [];

  constructor(sourceStore: SourceStore) {
    if (typeof window !== 'undefined') {
      this.initWebGA();
      this.androidInterface = window.AnalyticsWebInterface;
      if (this.isAndroidInterfaceAvailable) {
        window.onload = this.processPendingEvents;
      }
    }
    this.iosInterface = new IOSInterface('firebase');
    this.store = sourceStore;
  }

  get isAndroidInterfaceAvailable() {
    return Boolean(window.AnalyticsWebInterface);
  }

  get isIOSInterfaceAvailable() {
    return Boolean(window.webkit?.messageHandlers?.chatie);
  }

  protected transformData = (name: string, params: Params) => {
    return Object.keys(params).reduce((_params, key) => {
      const value = params[key];

      // big query integer issue fix but ignore ecommerce purchase event
      if (
        typeof value === 'number' &&
        name !== 'ecommerce_purchase' &&
        name !== 'purchase'
      ) {
        return {
          ..._params,
          [key]: value.toString(),
        };
      }

      return {
        ..._params,
        [key]: value,
      };
    }, {});
  };

  addPendingEvent = (name: keyof FirebaseEvents, data: string) => {
    this.pendingEvents.push({ name, data });
  };

  processPendingEvents = () => {
    this.pendingEvents.forEach(({ name, data }) => {
      if (this.isAndroidInterfaceAvailable && this.androidInterface?.logEvent) {
        this.androidInterface.logEvent(name, data);
      }
    });
    this.pendingEvents = [];
  };

  private initWebGA() {
    const host = window.location.host;
    const CHEMING_DOMAIN = process.env.NEXT_PUBLIC_CHEMING_DOMAIN;

    const chemingHost = CHEMING_DOMAIN ? new URL(CHEMING_DOMAIN).host : '';
    const isCheming = host === chemingHost;
    const domain = isCheming ? DOMAIN.cheming : DOMAIN.chatie;
    const gaKey = GA_KEYS[domain];
    if (!gaKey) {
      console.warn('GA 키 없음:', host);
      return;
    }

    if (typeof window.gtag !== 'function') {
      console.error('gtag가 로드되지 않았습니다.');
      return;
    }

    window.gtag('config', gaKey);
    console.log('GTAG INIT');
  }

  logEvent = <T extends keyof FirebaseEvents>(
    name: T,
    params: FirebaseEvents[T]
  ) => {
    const source = this.store.getState().source;
    const newParams = source ? { source, ...params } : params;
    const data = makeData(this.transformData(name, newParams as Params));

    if (typeof window !== 'undefined' && typeof window?.gtag === 'function') {
      window.gtag('event', name, this.transformData(name, { ...newParams }));
    }

    log('firebase', name, data);

    if (this.isAndroidInterfaceAvailable) {
      if (document.readyState !== 'complete') {
        this.pendingEvents.push({ name, data: data.android });
      } else if (this.androidInterface?.logEvent) {
        this.androidInterface?.logEvent(name, data.android);
      }
    } else if (this.isIOSInterfaceAvailable) {
      this.iosInterface.log('logEvent', name, { parameters: data.ios });
    }
  };

  setUserProperty = (name: string, value: string | number) => {
    log('firebase', 'setUserProperty', { name, value });

    if (this.isAndroidInterfaceAvailable) {
      this.androidInterface?.setUserProperty(name, value);
    } else if (this.isIOSInterfaceAvailable) {
      this.iosInterface.log('setUserProperty', name, { value });
    }
  };
}

/* oneSignal */

export type OneSignalEvents = {
  nickname: 'nickname';
  screen_view: 'screen_view';
};
class OneSignalInterface implements DataManagementInterface {
  androidInterface?: OneSignalWebInterface;
  iosInterface: IOSInterface;
  store: SourceStore;

  private static instance: OneSignalInterface;

  public static getInstance(sourceStore: SourceStore) {
    return this.instance || (this.instance = new this(sourceStore));
  }

  pendingEvents: PendingEvent[] = [];

  constructor(sourceStore: SourceStore) {
    if (typeof window !== 'undefined') {
      this.androidInterface = window.OneSignalWebInterface;

      if (this.isAndroidInterfaceAvailable) {
        window.onload = this.processPendingEvents;
      }
    }
    this.iosInterface = new IOSInterface('oneSignal');
    this.store = sourceStore;
  }

  get isAndroidInterfaceAvailable() {
    return Boolean(this.androidInterface);
  }

  get isIOSInterfaceAvailable() {
    return Boolean(window.webkit?.messageHandlers?.chatie);
  }

  addPendingEvent = (name: keyof OneSignalEvents, data: string) => {
    this.pendingEvents.push({ name, data });
  };

  processPendingEvents = () => {
    this.pendingEvents.forEach(({ name, data }) => {
      if (this.isAndroidInterfaceAvailable && this.androidInterface?.addTag) {
        this.androidInterface.addTag(name, data);
      }
    });
    this.pendingEvents = [];
  };

  addTag = (name: keyof OneSignalEvents, value: string) => {
    console.log('isIOSInterfaceAvailable', this.isIOSInterfaceAvailable);
    log('oneSignal', 'addTag', { name, value });

    if (this.isAndroidInterfaceAvailable) {
      this.androidInterface?.addTag(name, value);
    } else if (this.isIOSInterfaceAvailable) {
      this.iosInterface.log('addTag', name, { parameters: name, value });
    }
  };

  addTrigger = (name: keyof OneSignalEvents, value: string) => {
    log('oneSignal', 'addTrigger', { name, value });
    if (this.isAndroidInterfaceAvailable) {
      this.androidInterface?.addTrigger(name, value);
    } else if (this.isIOSInterfaceAvailable) {
      this.iosInterface.log('addTrigger', name, {
        parameters: name,
        value,
      });
    }
  };
}

export const useAnalytics = () => {
  const { platform, version } = useGlobalContext();

  const firebase = useRef(FirebaseInterface.getInstance(useSourceStore()));
  const oneSignal = useRef(OneSignalInterface.getInstance(useSourceStore()));

  return {
    firebase: firebase.current,
    oneSignal: checkRequiredAppVersion(platform, version, {
      iOS: '2.25.0',
      android: '2.30.0',
    })
      ? oneSignal?.current
      : undefined,
  };
};
